import * as turf from '@turf/turf'

/**
 * Maximum number of vertices allowed for Planet API subscriptions
 */
export const MAX_VERTICES = 1500

/**
 * Recursively counts vertices in a GeoJSON geometry
 * @param {Object} geometry - GeoJSON geometry object
 * @returns {number} Total number of vertices
 */
export function countVertices(geometry) {
  if (!geometry || !geometry.type) {
    return 0
  }

  switch (geometry.type) {
    case 'Point':
      return 1

    case 'LineString':
      return geometry.coordinates.length

    case 'Polygon':
      // Sum vertices from all rings (exterior + holes)
      return geometry.coordinates.reduce((sum, ring) => sum + ring.length, 0)

    case 'MultiPoint':
      return geometry.coordinates.length

    case 'MultiLineString':
      return geometry.coordinates.reduce(
        (sum, lineString) => sum + lineString.length,
        0
      )

    case 'MultiPolygon':
      // Sum vertices from all polygons, all rings
      return geometry.coordinates.reduce(
        (sum, polygon) =>
          sum + polygon.reduce((ringSum, ring) => ringSum + ring.length, 0),
        0
      )

    case 'GeometryCollection':
      return geometry.geometries.reduce(
        (sum, geom) => sum + countVertices(geom),
        0
      )

    default:
      console.warn(`Unknown geometry type: ${geometry.type}`)
      return 0
  }
}

/**
 * Validates that geometry is a supported type for Planet API
 * @param {Object} geometry - GeoJSON geometry object
 * @returns {Object} { valid: boolean, error: string | null }
 */
export function validateGeometryType(geometry) {
  if (!geometry || !geometry.type) {
    return { valid: false, error: 'Geometry is missing or has no type' }
  }

  const supportedTypes = ['Polygon', 'MultiPolygon']

  if (!supportedTypes.includes(geometry.type)) {
    return {
      valid: false,
      error: `Geometry type '${geometry.type}' is not supported. Only Polygon and MultiPolygon are allowed.`
    }
  }

  return { valid: true, error: null }
}

/**
 * Validates geometry complexity (vertex count)
 * @param {Object} geometry - GeoJSON geometry object
 * @param {number} maxVertices - Maximum allowed vertices (default: MAX_VERTICES)
 * @returns {Object} { valid: boolean, count: number, error: string | null }
 */
export function validateGeometryComplexity(geometry, maxVertices = MAX_VERTICES) {
  const count = countVertices(geometry)

  if (count > maxVertices) {
    return {
      valid: false,
      count,
      error: `Geometry has ${count.toLocaleString()} vertices, maximum is ${maxVertices.toLocaleString()}`
    }
  }

  return { valid: true, count, error: null }
}

/**
 * Merges multiple Polygon/MultiPolygon features into a single MultiPolygon
 * @param {Array} features - Array of GeoJSON Feature objects
 * @returns {Object|null} GeoJSON MultiPolygon geometry or null if invalid
 */
export function mergePolygons(features) {
  if (!features || features.length === 0) {
    return null
  }

  // Extract geometries from features
  const geometries = features
    .map(f => f.geometry)
    .filter(g => g && (g.type === 'Polygon' || g.type === 'MultiPolygon'))

  if (geometries.length === 0) {
    return null
  }

  // If only one geometry, normalize it to MultiPolygon if it's a Polygon
  if (geometries.length === 1) {
    const geom = geometries[0]
    if (geom.type === 'MultiPolygon') {
      return geom
    }
    // Convert Polygon to MultiPolygon
    return {
      type: 'MultiPolygon',
      coordinates: [geom.coordinates]
    }
  }

  // Merge multiple geometries into MultiPolygon
  const allPolygons = []

  for (const geom of geometries) {
    if (geom.type === 'Polygon') {
      allPolygons.push(geom.coordinates)
    } else if (geom.type === 'MultiPolygon') {
      allPolygons.push(...geom.coordinates)
    }
  }

  return {
    type: 'MultiPolygon',
    coordinates: allPolygons
  }
}

/**
 * Checks if a LineString is closed (forms a ring/circle)
 * @param {Object} geometry - GeoJSON geometry object
 * @returns {boolean} True if the LineString is closed
 */
export function isClosedLineString(geometry) {
  if (!geometry || geometry.type !== 'LineString') {
    return false
  }
  const coords = geometry.coordinates
  if (!coords || coords.length < 3) {
    return false
  }
  const first = coords[0]
  const last = coords[coords.length - 1]
  // Check if first and last points are the same (or very close)
  const threshold = 0.0001 // ~11 meters at equator
  return (
    Math.abs(first[0] - last[0]) < threshold &&
    Math.abs(first[1] - last[1]) < threshold
  )
}

/**
 * Converts a closed LineString to a Polygon
 * @param {Object} geometry - GeoJSON LineString geometry
 * @returns {Object} GeoJSON Polygon geometry
 */
export function closedLineStringToPolygon(geometry) {
  if (!geometry || geometry.type !== 'LineString') {
    return geometry
  }
  const coords = geometry.coordinates.map(c => c.slice(0, 2)) // Strip Z if present
  // Ensure ring is closed
  const first = coords[0]
  const last = coords[coords.length - 1]
  if (first[0] !== last[0] || first[1] !== last[1]) {
    coords.push([...first])
  }
  return {
    type: 'Polygon',
    coordinates: [coords]
  }
}

/**
 * Calculates area of a GeoJSON geometry in square meters
 * @param {Object} geometry - GeoJSON geometry object
 * @returns {number} Area in square meters
 */
export function calculateArea(geometry) {
  try {
    // Handle closed LineStrings (circles/buffers) as Polygons
    if (isClosedLineString(geometry)) {
      return turf.area(closedLineStringToPolygon(geometry))
    }
    return turf.area(geometry)
  } catch (e) {
    console.error('Error calculating area:', e)
    return 0
  }
}

/**
 * Formats area for display
 * @param {number} areaM2 - Area in square meters
 * @returns {string} Formatted area string (e.g., "342.5 km²" or "15.3 ha")
 */
export function formatArea(areaM2) {
  const areaKm2 = areaM2 / 1_000_000
  const areaHa = areaM2 / 10_000

  if (areaKm2 >= 1) {
    return `${areaKm2.toFixed(1)} km²`
  } else if (areaHa >= 0.1) {
    return `${areaHa.toFixed(1)} ha`
  } else {
    return `${areaM2.toFixed(0)} m²`
  }
}

/**
 * Simplifies geometry to reduce vertex count
 * @param {Object} geometry - GeoJSON geometry object
 * @param {number} tolerance - Simplification tolerance in kilometers (default: 0.001)
 * @param {boolean} highQuality - Use high quality simplification (default: true)
 * @returns {Object} Simplified GeoJSON geometry
 */
export function simplifyGeometry(geometry, tolerance = 0.001, highQuality = true) {
  try {
    const simplified = turf.simplify(geometry, {
      tolerance,
      highQuality
    })
    return simplified.geometry || simplified
  } catch (e) {
    console.error('Error simplifying geometry:', e)
    return geometry
  }
}

/**
 * Backend vertex limit for boundary geometries
 */
export const MAX_BOUNDARY_VERTICES = 10000

/**
 * Simplifies a GeoJSON feature's geometry if it exceeds the vertex limit.
 * Uses iterative tolerance increases until under the limit.
 * Returns the feature as-is if already under the limit.
 * @param {Object} feature - GeoJSON Feature object
 * @param {number} maxVertices - Maximum allowed vertices (default: MAX_BOUNDARY_VERTICES)
 * @returns {Object} Feature with simplified geometry (or original if under limit)
 */
export function autoSimplifyFeature(feature, maxVertices = MAX_BOUNDARY_VERTICES) {
  if (!feature || !feature.geometry) return feature

  const vertices = countVertices(feature.geometry)
  if (vertices <= maxVertices) return feature

  let tolerance = 0.0001
  let simplified = feature.geometry
  for (let i = 0; i < 20; i++) {
    simplified = simplifyGeometry(feature.geometry, tolerance, true)
    if (countVertices(simplified) <= maxVertices) break
    tolerance *= 2.5
  }

  return {
    ...feature,
    geometry: simplified,
    properties: {
      ...feature.properties,
      _simplified: true,
      _originalVertices: vertices
    }
  }
}

/**
 * Extracts feature name from properties or generates default
 * @param {Object} feature - GeoJSON Feature object
 * @param {number} index - Feature index for default naming
 * @returns {string} Feature name
 */
export function getFeatureName(feature, index) {
  if (!feature) return `Feature ${index + 1}`

  // Try common name properties
  const props = feature.properties || {}
  return (
    props.name ||
    props.Name ||
    props.NAME ||
    props.title ||
    props.Title ||
    props.id ||
    props.ID ||
    `Feature ${index + 1}`
  )
}

/**
 * Counts total features in a structure (handles nested collections)
 * @param {Array} features - Array of features or feature collections
 * @returns {number} Total feature count
 */
export function countFeatures(features) {
  if (!features || !Array.isArray(features)) {
    return 0
  }

  return features.reduce((count, item) => {
    if (item.type === 'FeatureCollection') {
      return count + countFeatures(item.features)
    }
    return count + 1
  }, 0)
}

/**
 * Validates that merged geometry doesn't exceed limits
 * @param {Array} features - Array of features to merge
 * @returns {Object} { valid: boolean, totalVertices: number, geometry: Object|null, error: string|null }
 */
export function validateAndMergeGeometries(features) {
  if (!features || features.length === 0) {
    return {
      valid: false,
      totalVertices: 0,
      geometry: null,
      error: 'No features selected'
    }
  }

  // Merge geometries
  const merged = mergePolygons(features)

  if (!merged) {
    return {
      valid: false,
      totalVertices: 0,
      geometry: null,
      error: 'Failed to merge geometries. Ensure all features are Polygons or MultiPolygons.'
    }
  }

  // Validate type
  const typeValidation = validateGeometryType(merged)
  if (!typeValidation.valid) {
    return {
      valid: false,
      totalVertices: 0,
      geometry: merged,
      error: typeValidation.error
    }
  }

  // Validate complexity
  const complexityValidation = validateGeometryComplexity(merged)
  if (!complexityValidation.valid) {
    return {
      valid: false,
      totalVertices: complexityValidation.count,
      geometry: merged,
      error: complexityValidation.error
    }
  }

  return {
    valid: true,
    totalVertices: complexityValidation.count,
    geometry: merged,
    error: null
  }
}
